import { Box, Button, Chip, Stack, TabList, Tabs, Tooltip, Typography } from "@mui/joy";
import { Outlet, useChildMatches } from "@tanstack/react-router";
import { useMemo } from "react";

import { useChallengeQuery, useUpdateChallengeMutation } from "@/hooks/challenges";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { TabLink } from "../Links";
import ChallengeStepper from "./ChallengeStepper";

const useTabFromPath = <TTabList extends string[]>({
  basePath,
  tabs,
  defaultTab,
}: {
  basePath: string;
  tabs: TTabList;
  defaultTab: TTabList[number];
}): TTabList[number] => {
  const tab = useChildMatches({
    select: (matches) => {
      return matches[0]?.routeId.replace(basePath + "/", "");
    },
  });
  return tab && tabs.includes(tab) ? tab : defaultTab;
};

const ChallengeTabsNavigation = ({
  challengeUuid,
  tab,
}: {
  challengeUuid?: string;
  tab: string;
}) => {
  return (
    <Tabs
      value={tab}
      sx={{
        paddingX: 1.5,
        paddingTop: 0.5,
        ".MuiTab-root": {
          flex: 1,
        },
      }}
    >
      <TabList>
        <TabLink
          value="details"
          to={challengeUuid ? "/gym/$venueUuid/challenges/$challengeUuid/profile/details" : "."}
          replace
        >
          Details
        </TabLink>
        <TabLink
          value="rules"
          from="/gym/$venueUuid/challenges/$challengeUuid/profile"
          to="./rules"
          replace
          disabled={!challengeUuid}
        >
          Rules & rewards
        </TabLink>
        <TabLink
          value="promotion"
          from="/gym/$venueUuid/challenges/$challengeUuid/profile"
          to="./promotion"
          replace
          disabled={!challengeUuid}
        >
          Promotion
        </TabLink>
      </TabList>
    </Tabs>
  );
};

export default function ChallengeProfile({
  challengeUuid,
  children,
  isCreating = false,
}: {
  challengeUuid?: string;
  isCreating?: boolean;
  children?: React.ReactNode;
}) {
  const venueUuid = useCurrentVenueUuid();
  const { data: challenge } = useChallengeQuery(challengeUuid, venueUuid);

  const tab = useTabFromPath({
    basePath: "/gym/$venueUuid/challenges/$challengeUuid/profile",
    tabs: ["details", "rules", "promotion"] as const,
    defaultTab: "details",
  });

  const publishMutation = useUpdateChallengeMutation(challengeUuid, venueUuid);

  // Simple validation - can expand this later
  const hasValidationErrors = useMemo(() => {
    if (!challenge) return true;
    return !challenge.name || !challenge.startTime;
  }, [challenge]);

  const publishButton = (
    <Button
      size="lg"
      color="primary"
      disabled={hasValidationErrors}
      loading={publishMutation.isPending}
      loadingPosition="start"
      onClick={() =>
        publishMutation.mutate(
          { isHidden: false },
          {
            onError: () => alert("Unknown error occurred. Please try again or contact support"),
          },
        )
      }
    >
      Publish
    </Button>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        backgroundColor: "neutral.200",
        padding: 1.5,
      }}
    >
      {challenge?.isHidden ? (
        <Stack
          sx={{
            border: "2px solid",
            borderColor: "neutral.400",
            backgroundColor: "white",
            maxWidth: 800,
            borderRadius: 8,
            padding: 1.5,
            marginBottom: 2,
          }}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Chip size="lg" sx={{ fontWeight: "bold" }} variant="solid">
              Draft mode
            </Chip>
            <Typography color="neutral" level="body-sm">
              The challenge is only visible to staff members
            </Typography>
          </Box>
          {hasValidationErrors ? (
            <Tooltip
              title="To publish the challenge, finish going through the tabs and fix any issues"
              size="md"
              placement="bottom"
            >
              <Box>{publishButton}</Box>
            </Tooltip>
          ) : (
            publishButton
          )}
        </Stack>
      ) : null}

      <Stack
        sx={{
          backgroundColor: "white",
          borderRadius: 8,
          overflow: "hidden",
          flex: 1,
          maxWidth: 800,
        }}
      >
        {isCreating || challenge?.isHidden ? (
          <ChallengeStepper
            challenge={challenge}
            activeStep={tab as "details" | "rules" | "promotion"}
          />
        ) : (
          <ChallengeTabsNavigation challengeUuid={challengeUuid} tab={tab} />
        )}
        {children || <Outlet />}
      </Stack>
    </Box>
  );
}
